import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { KeyboardArrowDown } from '@mui/icons-material';
import { Collapse, Skeleton, Typography } from '@mui/material';

import useDiffAsync from 'hooks/use-diff-async';
import MarkdownElement from 'main/components/utils/MarkdownElement';
import ProtectedData from 'pages/item/v2/components/ProtectedData';
import useHistoryOfChangesContext from 'pages/item/v2/hooks/use-history-of-changes-context';
import Html from 'utils/Html';
import isEmpty from 'utils/is-empty';

import useStyles from './styles';

interface Props {
    description: string;
    privateArea: number;
}

const LINE_HEIGHT = 25;

const Description: React.FC<Props> = ({ description, privateArea }) => {
    const { classes, cx } = useStyles();
    const containerRef = useRef<HTMLDivElement>(null);

    const [open, setOpen] = useState(false);
    const [collapseSize, setCollapseSize] = useState(0);
    const { revision } = useHistoryOfChangesContext();

    /** replace for markdown wrong parsed Bold text */
    const refineMd = useCallback((md: string) => md.replace(/\*\*\s?(.+?)\s\*\*/g, '**$1**'), []);
    const [diff, isComputing] = useDiffAsync({ method: 'words' });
    const [value, setValue] = useState(() => ({ type: 'default', text: refineMd(description ?? '') }));

    useEffect(() => {
        const newValue = refineMd(description ?? '');
        setValue({ type: 'default', text: newValue });

        if (revision && 'description' in revision.changes) {
            const oldValue = refineMd(revision.changes.description ?? '');

            if (oldValue !== newValue) {
                diff(oldValue, newValue)
                    .then((output) =>
                        output.reduce((acc, { value, added, removed }) => {
                            if (added) return `${acc}<span class=${classes.added}>${value}</span>`;
                            if (removed) return `${acc}<span class=${classes.removed}>${value}</span>`;
                            return `${acc}${value}`;
                        }, '')
                    )
                    .then((text) => setValue({ type: 'diff', text }));
            }
        }
    }, [classes.added, classes.body, classes.removed, description, diff, refineMd, revision]);

    useEffect(() => {
        if (containerRef.current && containerRef.current.clientHeight > window.innerHeight) {
            setCollapseSize(window.innerHeight);
        }
    }, []);

    const content = useMemo(() => {
        let data: JSX.Element | null = null;
        switch (true) {
            case isEmpty(value.text):
                data = null;
                break;
            case value.type === 'default':
                data = <MarkdownElement className={classes.body} text={value.text} />;
                break;
            case value.type === 'diff':
                data = <Html className={classes.body} html={value.text} />;
                break;
            default:
                data = null;
                break;
        }

        if (data === null) return null;
        if (isComputing) {
            const height = containerRef.current?.clientHeight ?? LINE_HEIGHT;
            const length = Math.floor(height / LINE_HEIGHT);

            return (
                <div>
                    {Array.from({ length }, () => (
                        <Skeleton variant="text" sx={{ lineHeight: `${LINE_HEIGHT}px` }} />
                    ))}
                </div>
            );
        }

        return <ProtectedData data={description}>{data}</ProtectedData>;
    }, [classes.body, description, isComputing, value.text, value.type]);

    return content ? (
        <div className={classes.description} ref={containerRef}>
            {collapseSize ? (
                <>
                    <Collapse
                        in={open}
                        collapsedSize={collapseSize}
                        classes={{ root: cx({ [classes.mask]: !!privateArea || !open }) }}
                    >
                        {content}
                    </Collapse>
                    <div className={classes.more} onClick={() => setOpen(!open)}>
                        <Typography variant="body2">{open ? 'Less' : 'More'}</Typography>
                        <KeyboardArrowDown
                            className={classes.icon}
                            style={{ transform: open ? 'rotate(-180deg)' : 'initial' }}
                        />
                    </div>
                </>
            ) : (
                content
            )}
        </div>
    ) : null;
};

export default Description;
